import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  ArrowLeft, Star, CheckCircle, MapPin, Phone, Mail,
  Globe, Clock, ExternalLink, Share2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, fr, en) => lang === 'fr' ? fr : en;

const DAYS = {
  fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

const DEFAULT_HOURS = {
  mon: '9:00 - 17:00', tue: '9:00 - 17:00', wed: '9:00 - 17:00',
  thu: '9:00 - 17:00', fri: '9:00 - 17:00', sat: '10:00 - 15:00', sun: 'Ferme / Closed',
};

export default function BusinessDetailPage() {
  const { slug } = useParams();
  const { language, toggleLanguage } = useLanguage();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/directory/businesses/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setBusiness(data);
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-3 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
        <p className="text-[#4B5563] text-lg">{t(language, 'Entreprise introuvable', 'Business not found')}</p>
        <Link to="/browse" className="text-[#002FA7] font-semibold hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> {t(language, 'Retour au repertoire', 'Back to directory')}
        </Link>
      </div>
    );
  }

  const hours = business.hours || DEFAULT_HOURS;
  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Satoshi', 'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/60" data-testid="detail-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/browse" className="flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#002FA7] font-medium"
            data-testid="detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> {t(language, 'Retour', 'Back')}
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-md overflow-hidden text-xs font-semibold">
              <button onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-2.5 py-1.5 ${language === 'fr' ? 'bg-[#002FA7] text-white' : 'text-gray-500'}`}>FR</button>
              <button onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-2.5 py-1.5 ${language === 'en' ? 'bg-[#002FA7] text-white' : 'text-gray-500'}`}>EN</button>
            </div>
            <button className="p-2 rounded-lg hover:bg-gray-100" data-testid="share-btn">
              <Share2 className="w-4 h-4 text-[#4B5563]" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden" data-testid="detail-hero-image">
        <img src={business.image_url} alt={business.name}
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10 pb-16" data-testid="detail-content">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          {/* Main Info */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {business.is_verified ? (
                    <span className="inline-flex items-center gap-1 bg-[#10B981]/10 text-[#10B981] text-xs font-semibold px-2.5 py-1 rounded-full"
                      data-testid="verified-badge">
                      <CheckCircle className="w-3.5 h-3.5" /> {t(language, 'Verifie', 'Verified')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-[#6B7280] text-xs font-semibold px-2.5 py-1 rounded-full"
                      data-testid="invited-badge">
                      {t(language, 'Invite', 'Invited')}
                    </span>
                  )}
                  {business.rating > 0 && (
                    <span className="inline-flex items-center gap-1 bg-[#FFB300]/10 text-[#B07C00] text-xs font-semibold px-2.5 py-1 rounded-full">
                      <Star className="w-3.5 h-3.5 fill-current" /> {business.rating} ({business.review_count} {t(language, 'avis', 'reviews')})
                    </span>
                  )}
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A0A0A] tracking-tight"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  data-testid="business-name">
                  {business.name}
                </h1>
                <p className="text-[#4B5563] mt-1 text-sm font-medium">
                  <span className="capitalize">{business.category.replace('-', ' ')}</span>
                  {business.city && (
                    <span className="text-gray-400"> &middot; {business.city}{business.neighborhood ? ` (${business.neighborhood})` : ''}</span>
                  )}
                </p>
              </div>

              {business.is_verified && business.plan && business.plan !== 'free' && (
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                  business.plan === 'platinum' ? 'bg-gray-900 text-white' :
                  business.plan === 'gold' ? 'bg-[#FFB300]/20 text-[#B07C00]' :
                  'bg-gray-200 text-gray-600'
                }`}
                  data-testid="business-plan-badge">
                  {business.plan}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-[#4B5563] leading-relaxed text-base mb-8" data-testid="business-description">
              {language === 'fr' ? business.description_fr : business.description_en}
            </p>

            {/* Contact Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {business.address && (
                <div className="flex items-start gap-3 p-4 bg-[#F3F4F6] rounded-xl" data-testid="business-address">
                  <MapPin className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1">{t(language, 'Adresse', 'Address')}</p>
                    <p className="text-sm text-[#0A0A0A]">{business.address}</p>
                  </div>
                </div>
              )}
              {business.phone && (
                <div className="flex items-start gap-3 p-4 bg-[#F3F4F6] rounded-xl" data-testid="business-phone">
                  <Phone className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1">{t(language, 'Telephone', 'Phone')}</p>
                    <a href={`tel:${business.phone}`} className="text-sm text-[#002FA7] font-medium hover:underline">{business.phone}</a>
                  </div>
                </div>
              )}
              {business.email && (
                <div className="flex items-start gap-3 p-4 bg-[#F3F4F6] rounded-xl" data-testid="business-email">
                  <Mail className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1">{t(language, 'Courriel', 'Email')}</p>
                    <a href={`mailto:${business.email}`} className="text-sm text-[#002FA7] font-medium hover:underline">{business.email}</a>
                  </div>
                </div>
              )}
              {business.website && (
                <div className="flex items-start gap-3 p-4 bg-[#F3F4F6] rounded-xl" data-testid="business-website">
                  <Globe className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1">{t(language, 'Site web', 'Website')}</p>
                    <a href={business.website} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-[#002FA7] font-medium hover:underline flex items-center gap-1">
                      {business.website.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Hours */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="font-bold text-[#0A0A0A] text-lg mb-4 flex items-center gap-2"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                <Clock className="w-5 h-5 text-[#002FA7]" />
                {t(language, "Heures d'ouverture", 'Business Hours')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {dayKeys.map((key, i) => (
                  <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-sm">
                    <span className="text-[#4B5563] font-medium">{DAYS[language]?.[i] || DAYS.en[i]}</span>
                    <span className="text-[#0A0A0A]">{hours[key] || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div className="mt-8 text-center">
          <Link to="/browse" className="text-[#002FA7] font-semibold text-sm hover:underline flex items-center gap-1 justify-center"
            data-testid="detail-browse-back">
            <ArrowLeft className="w-4 h-4" /> {t(language, 'Retour au repertoire', 'Back to directory')}
          </Link>
        </div>
      </div>
    </div>
  );
}
